import { takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormArray,
} from '@angular/forms';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { add, differenceInCalendarDays } from 'date-fns';
import { ContactSMALLid } from 'src/app/components/contact/store/contact.model';
import { fromUnixTime } from 'date-fns/esm';

@Component({
  selector: 'app-contact-validator',
  templateUrl: './contact-validator.component.html',
  styleUrls: ['./contact-validator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactValidatorComponent implements OnInit, OnDestroy {
  defaultColor: string = '#d9d9d9';
  today = new Date(Date.now());
  subscribe = new Subject();
  roleForm: UntypedFormGroup = this.fb.group({});
  role: UntypedFormControl = new UntypedFormControl();
  isParrain: UntypedFormControl = new UntypedFormControl(false);
  isFinanceur: UntypedFormControl = new UntypedFormControl(false);
  isPrescripteur: UntypedFormControl = new UntypedFormControl(false);
  isSupport: UntypedFormControl = new UntypedFormControl(false);
  isTitulaire: UntypedFormControl = new UntypedFormControl(false);
  isSousTraitant: UntypedFormControl = new UntypedFormControl(false);
  isCoTraitant: UntypedFormControl = new UntypedFormControl(false);
  isPilotage: UntypedFormControl = new UntypedFormControl(false);
  isAssistance: UntypedFormControl = new UntypedFormControl(false);
  isAutre: UntypedFormControl = new UntypedFormControl(false);
  isControl: UntypedFormControl = new UntypedFormControl(false);
  autrePrecision: UntypedFormControl = new UntypedFormControl('');
  titulaire: UntypedFormControl = new UntypedFormControl('');
  hoursBenevolat: UntypedFormControl = new UntypedFormControl(0);
  dateStartIntervention: UntypedFormControl = new UntypedFormControl(this.today);
  dateEndIntervention: UntypedFormControl = new UntypedFormControl(
    add(this.today, { months: 6 })
  );
  durationIntervention: UntypedFormControl = new UntypedFormControl(6);
  radioValue: any;
  duration: UntypedFormControl = new UntypedFormControl(6);

  disabledDate = (current: Date): boolean =>
    // Can not select days before today and today
    differenceInCalendarDays(this.today, current) > 0;

  @Input() contacts: ContactSMALLid[] | any = [];
  @Output() cancel = new EventEmitter<boolean>(false);
  @Output() validate = new EventEmitter<any>(false);

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.form();
  }

  ngOnDestroy(): void {
    this.subscribe.next(null);
    this.subscribe.complete();
  }

  form(): void {
    this.formInit();
    this.formCreate();
  }

  formInit(): void {
    this.role = new UntypedFormControl('', Validators.required);
    this.isParrain = new UntypedFormControl(false);
    this.isFinanceur = new UntypedFormControl(false);
    this.isPrescripteur = new UntypedFormControl(false);
    this.isSupport = new UntypedFormControl(false);
    this.isTitulaire = new UntypedFormControl(false);
    this.isSousTraitant = new UntypedFormControl(false);
    this.isCoTraitant = new UntypedFormControl(false);

    this.isPilotage = new UntypedFormControl(false);
    this.isAssistance = new UntypedFormControl(false);
    this.isControl = new UntypedFormControl(false);
    this.dateStartIntervention = new UntypedFormControl(this.today);
    this.dateEndIntervention = new UntypedFormControl(add(this.today, { months: 6 }));
    this.durationIntervention = new UntypedFormControl(6);
    this.hoursBenevolat = new UntypedFormControl(0);
    this.isAutre = new UntypedFormControl(false);
    this.duration = new UntypedFormControl(6);
    this.autrePrecision = new UntypedFormControl('');
    this.titulaire = new UntypedFormControl('');
  }

  formCreate(): void {
    this.radioValue = 'Parrainage';
    this.roleForm = this.fb.group({
      role: this.role,
      isParrain: this.isParrain,
      isFinanceur: this.isFinanceur,
      isPrescripteur: this.isPrescripteur,
      isSupport: this.isSupport,
      isTitulaire: this.isTitulaire,
      isSousTraitant: this.isSousTraitant,
      isCoTraitant: this.isCoTraitant,

      isPilotage: this.isPilotage,
      isAssistance: this.isAssistance,
      isControl: this.isControl,
      dateStartIntervention: this.dateStartIntervention,
      dateEndIntervention: this.dateEndIntervention,
      durationIntervention: this.durationIntervention,
      hoursBenevolat: this.hoursBenevolat,
      duration: this.duration,
      isAutre: this.isAutre,
      autrePrecision: this.autrePrecision,
      titulaire: this.titulaire,
    });
  }

  onSetDateStart(): void {
    this.roleForm
      .get('dateStartIntervention')
      ?.valueChanges.subscribe((date: Date) => {
        if (!date) {
          return;
        } else {
          this.roleForm.patchValue({
            dateEndIntervention: add(date, { months: 2 }),
          });
        }
      });
  }

  onUpdateDate(start: any, end: any, duration: number): void {
    if (!start || !end) return;

    this.roleForm = this.fb.group({
      dateStartIntervention: fromUnixTime(start['seconds']),
      dateEndIntervention: fromUnixTime(end['seconds']),
      durationIntervention: duration,
    });
  }

  onSetRole(event: any): void {
    switch (event) {
      case 'financeur':
        this.roleForm.patchValue({
          isFinanceur: true,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isCoTraitant: false,
          isPilotage: false,
          isAssistance: false,
          isControl: false,
          isAutre: false,
          autrePrecision: '',
          titulaire: null,
        });
        break;
      case 'prescripteur':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: true,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isPilotage: false,
          isAssistance: false,
          isCoTraitant: false,

          isControl: false,
          isAutre: false,

          autrePrecision: '',
          titulaire: null,
        });
        break;
      case 'support':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: true,
          isTitulaire: false,
          isSousTraitant: false,
          isCoTraitant: false,

          isPilotage: false,
          isAssistance: false,
          isControl: false,
          isAutre: false,

          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'titulaire':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: true,
          isSousTraitant: false,
          isCoTraitant: false,

          isPilotage: false,
          isAssistance: false,
          isControl: false,
          isAutre: false,

          autrePrecision: '',
        });

        break;
      case 'parrainage':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: true,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isCoTraitant: false,

          isPilotage: false,
          isAssistance: false,
          isControl: false,
          isAutre: false,

          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'sousTraitant':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: true,
          isPilotage: false,
          isCoTraitant: false,

          isAssistance: false,
          isControl: false,
          isAutre: false,

          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'pilotage':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isPilotage: true,
          isAssistance: false,
          isControl: false,
          isAutre: false,
          isCoTraitant: false,

          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'assistance':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isPilotage: false,
          isAssistance: true,
          isControl: false,
          isAutre: false,
          isCoTraitant: false,

          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'control':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isPilotage: false,
          isAssistance: false,
          isControl: true,
          isAutre: false,
          isCoTraitant: false,

          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'cotraitant':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isPilotage: false,
          isAssistance: false,
          isControl: false,
          isCoTraitant: true,

          isAutre: false,
          autrePrecision: '',
          titulaire: null,
        });

        break;
      case 'autre':
        this.roleForm.patchValue({
          isFinanceur: false,
          isParrain: false,
          isPrescripteur: false,
          isSupport: false,
          isTitulaire: false,
          isSousTraitant: false,
          isPilotage: false,
          isAssistance: false,
          isControl: false,
          isCoTraitant: false,

          isAutre: true,
          autrePrecision: '',
          titulaire: null,
        });

        break;
      default:
        break;
    }
  }

  onValidate(): void {
    if (!this.roleForm.valid) {
      return;
    } else {
      const contacts = this.roleForm.value;
      this.validate.emit(contacts);
      this.roleForm.reset();
    }
  }

  onCancel(): void {
    this.cancel.emit(true);
    this.roleForm.reset();
  }
}
